<template>
  <v-container fluid class="down-top-padding okr-dashboard">
    <BaseBreadcrumb :title="page.title" :icon="page.icon" :breadcrumbs="breadcrumbs" height="55px"></BaseBreadcrumb>

    <section class="okr-dashboard__header">
      <div class="okr-dashboard__header-copy">
        <span class="okr-dashboard__eyebrow">OKR</span>
        <h1>Dashboard de OKR</h1>
        <p>Resumen de actividades y cumplimiento</p>
      </div>

      <div class="okr-dashboard__filters">
        <div class="okr-dashboard__filter">
          <v-select
            dense
            outlined
            hide-details="auto"
            v-model="anio"
            :items="listAnios"
            @change="cambioAnio"
            no-data-text="No se encontró información"
            item-text="text"
            item-value="value"
            label="Año"
          ></v-select>
        </div>
        <div class="okr-dashboard__filter">
          <v-select
            dense
            outlined
            hide-details="auto"
            v-model="semana"
            :items="semanas"
            no-data-text="No se encontró información"
            item-text="text"
            item-value="value"
            label="Semana"
          ></v-select>
        </div>
      </div>
    </section>

    <v-row v-auth-acl="'gh-okr-dash-read'">
      <v-col v-for="kpi in kpiCards" :key="kpi.label" cols="12" sm="6" lg="4">
        <article
          :class="['okr-dashboard__kpi', `okr-dashboard__kpi--${kpi.tone}`]"
          :title="kpi.help"
          :aria-label="kpi.help"
        >
          <div :class="['okr-dashboard__kpi-icon', kpi.tone]">
            <v-icon>{{ kpi.icon }}</v-icon>
          </div>
          <div>
            <span>{{ kpi.label }}</span>
            <strong>{{ kpi.value }}</strong>
          </div>
        </article>
      </v-col>

      <v-col cols="12" lg="6">
        <CumplimientoPL :datos="datos"></CumplimientoPL>
      </v-col>
      <v-col cols="12" lg="6">
        <CantidadRNR :datos="datos"></CantidadRNR>
      </v-col>

      <v-col cols="12">
        <div class="okr-dashboard__section-title">
          <h2>Desempeño por área</h2>
          <p>Comparativo de actividades y cumplimiento por área o subárea.</p>
        </div>
      </v-col>

      <v-col cols="12" lg="6">
        <CantidadRNRXArea :datos="datos"></CantidadRNRXArea>
      </v-col>
      <v-col cols="12" lg="6">
        <CumplimientoPLXArea :datos="datos"></CumplimientoPLXArea>
      </v-col>
    </v-row>

    <div v-if="showLoading" class="progress-background">
      <v-progress-circular :size="70" :width="7" color="primary" indeterminate></v-progress-circular>
    </div>
  </v-container>
</template>

<script>
import DashboardService from '@/giplan/okr/dashboard/services/DashboardService';
import moment from 'moment';
import { decryptAES } from '@/components4x/utils/utils4x';
export default {
  name: 'Dashboard',
  data: () => ({
    semana: '',
    semanas: [],
    datos: {},
    showLoading: false,
    dashboardService: null,
    page: {
      title: 'OKR'
    },
    breadcrumbs: [
      {
        text: 'OKR',
        disabled: true
      },
      {
        text: 'Dashboard',
        disabled: false
      }
    ],
    anio: new Date().getFullYear().toString(),
    listAnios: []
  }),
  computed: {
    totalSemana() {
      if (!this.datos.totaltareassemana || this.datos.totaltareassemana.length === 0) {
        return {};
      }

      return this.datos.totaltareassemana[0];
    },
    filasAreas() {
      return this.datos.totaltareasareas || [];
    },
    actividadesEvaluadas() {
      const realizadas = Number(this.totalSemana.totaltareasrealizadas) || 0;
      const noRealizadas = Number(this.totalSemana.totaltareasnorealizadas) || 0;

      return realizadas + noRealizadas;
    },
    actividadesRealizadas() {
      return Number(this.totalSemana.totaltareasrealizadas) || 0;
    },
    cumplimientoGeneral() {
      let total = 0;
      let cantidad = 0;

      this.filasAreas.forEach((element) => {
        const realizadas = Number(element.actividadesrealizadas) || 0;
        const noRealizadas = Number(element.actividadesnorealizadas) || 0;

        if (realizadas + noRealizadas <= 0) {
          return;
        }

        cantidad++;
        total += Number(element.cumplimiento) || 0;
      });

      return cantidad > 0 ? parseInt(total / cantidad) : 0;
    },
    kpiCards() {
      return [
        {
          label: 'Actividades evaluadas',
          value: this.actividadesEvaluadas,
          icon: 'mdi-calendar-check-outline',
          tone: 'primary',
          help: 'Total de actividades mostradas en la gráfica de la semana: realizadas más no realizadas.'
        },
        {
          label: 'Actividades realizadas',
          value: this.actividadesRealizadas,
          icon: 'mdi-check-circle-outline',
          tone: 'achieved',
          help: 'Actividades marcadas como realizadas en el resumen semanal.'
        },
        {
          label: 'Cumplimiento',
          value: `${this.cumplimientoGeneral}%`,
          icon: 'mdi-chart-donut',
          tone: 'progress',
          help: 'Promedio de cumplimiento calculado con las areas que tienen actividades realizadas o no realizadas.'
        }
      ];
    }
  },
  watch: {
    async semana() {
      await this.initialize();
    }
  },
  components: {
    CumplimientoPLXArea: () => import('../components/CumplimientoPLXArea'),
    CantidadRNRXArea: () => import('../components/CantidadRNRXArea'),
    CantidadRNR: () => import('../components/CantidadRNR'),
    CumplimientoPL: () => import('../components/CumplimientoPL')
  },
  methods: {
    async initialize() {
      this.datos = {};
      this.showLoading = true;
      const emp = decryptAES(localStorage.getItem('emp'));
      const res = await this.dashboardService.get().execResource('dashboard', {
        idempresa: emp,
        semana: this.semana,
        anio: this.anio
      });
      this.datos = res;
      this.showLoading = false;
    },
    async semanasItems() {
      this.showLoading = true;
      this.semana = [];
      const res = await this.dashboardService.get().execResource('fecha', {
        fecha: moment().year() + '-12-31'
      });
      for (let i = 1; i <= res; i++) {
        this.semanas.push(i);
      }
      this.semana = await this.dashboardService.get().execResource('fecha', {
        fecha: this.anio == moment().year() ? moment().format('YYYY-MM-DD') : moment().year() + '-12-31'
      });
      this.showLoading = false;
    },
    async cargarAnios() {
      this.listAnios = [];
      this.showLoading = true;
      const emp = decryptAES(localStorage.getItem('emp'));
      const res = await this.dashboardService.get().execResource('cargarAnios', {
        idempresa: emp
      });
      this.listAnios = res.map((el) => {
        return { value: el.anio, text: el.anio };
      });
      this.showLoading = false;
    },
    async cambioAnio() {
      await this.semanasItems();
      await this.initialize();
    }
  },
  async created() {
    this.dashboardService = this.$httpService(new DashboardService(), this);
    await this.cargarAnios();
    await this.semanasItems();
    await this.initialize();
  }
};
</script>

<style lang="scss" scoped>
.okr-dashboard {
  --okr-amber: #b45309;
  --okr-amber-soft: #fff7ed;
  --okr-border: #e5e8ef;
  --okr-muted: #6b7280;
  --okr-success: #10b981;
  --okr-success-soft: #e8fff5;
  --okr-text: #1f2937;
  padding-bottom: 84px !important;
}

.okr-dashboard__header {
  align-items: center;
  background: #fff;
  border: 1px solid var(--okr-border);
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 18px 20px;
}

.okr-dashboard__header-copy {
  min-width: 220px;

  h1 {
    color: var(--okr-text);
    font-size: 24px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1.2;
    margin: 2px 0 4px;
  }

  p {
    color: var(--okr-muted);
    font-size: 14px;
    margin: 0;
  }
}

.okr-dashboard__eyebrow {
  color: var(--v-primary-base);
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.okr-dashboard__filters {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
}

.okr-dashboard__filter {
  width: 170px;
}

.okr-dashboard__kpi {
  align-items: center;
  background: #fff;
  border: 1px solid var(--okr-border);
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
  display: flex;
  gap: 14px;
  min-height: 92px;
  padding: 16px;
  cursor: help;
  position: relative;

  &::before {
    border-radius: 8px 0 0 8px;
    content: '';
    height: calc(100% - 24px);
    left: 0;
    position: absolute;
    top: 12px;
    width: 3px;
  }

  span {
    color: var(--okr-muted);
    display: block;
    font-size: 13px;
    line-height: 1.25;
    margin-bottom: 4px;
  }

  strong {
    color: var(--okr-text);
    display: block;
    font-size: 28px;
    font-weight: 800;
    letter-spacing: 0;
    line-height: 1;
  }
}

.okr-dashboard__kpi-icon {
  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  flex: 0 0 44px;
  height: 44px;
  justify-content: center;
  width: 44px;

  &.primary,
  &.primary-soft {
    background: var(--v-primary-lighten5, rgba(207, 18, 55, 0.08));

    .v-icon {
      color: var(--v-primary-base) !important;
    }
  }

  &.achieved {
    background: var(--okr-success-soft);

    .v-icon {
      color: var(--okr-success) !important;
    }
  }

  &.progress {
    background: var(--okr-amber-soft);

    .v-icon {
      color: var(--okr-amber) !important;
    }
  }
}

.okr-dashboard__kpi--primary::before {
  background: var(--v-primary-base);
}

.okr-dashboard__kpi--achieved::before {
  background: var(--okr-success);
}

.okr-dashboard__kpi--progress::before {
  background: var(--okr-amber);
}

.okr-dashboard__section-title {
  margin: 8px 0 -2px;

  h2 {
    color: var(--okr-text);
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0;
    margin: 0 0 4px;
  }

  p {
    color: var(--okr-muted);
    font-size: 14px;
    margin: 0;
  }
}

@media (max-width: 720px) {
  .okr-dashboard__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .okr-dashboard__filters,
  .okr-dashboard__filter {
    width: 100%;
  }
}
</style>
